import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function ModelTrendChart({ data, model }) {
  // Extract trend data from evaluations
  const trendData = React.useMemo(() => {
    if (!data || !model || !data.stats.models[model] || !data.stats.models[model].evaluations) {
      return [];
    }

    const evaluations = data.stats.models[model].evaluations;

    // Skip processing if no evaluations
    if (!evaluations.length) return [];

    try {
      // Group by date
      const dateGroups = {};

      evaluations.forEach(val => {
        if (val.job_name) {
          // Match YYYY.MM.DD pattern
          const match = val.job_name.match(/(\d{4})\.(\d{2})\.(\d{2})/);

          if (match) {
            const [_, year, month, day] = match;
            // Store raw date for sorting
            const rawDateStr = `${year}-${month}-${day}`;
            // Format as MM/DD/YYYY for display
            const dateStr = `${month}/${day}/${year}`;

            if (!dateGroups[dateStr]) {
              dateGroups[dateStr] = {
                date: dateStr,
                rawDate: rawDateStr,
                entries: []
              };
            }

            // Better handling of number conversion
            const parseMetricValue = (value) => {
              // If value is undefined or null, return null to indicate missing data
              if (value === undefined || value === null) return null;

              // Try to convert to number
              const num = Number(value);

              // If it's NaN, return null, otherwise return the number (including 0)
              return isNaN(num) ? null : num;
            };

            // Add this evaluation to the date's entries, properly handling nulls vs zeros
            dateGroups[dateStr].entries.push({
              structure: parseMetricValue(val.structured_score),
              coverage: parseMetricValue(val.point_coverage_score),
              length: parseMetricValue(val.note_length_score),
              faithfulness: parseMetricValue(val.faithfulness_score)
            });
          }
        }
      });

      // Calculate averages for each date, properly handling nulls
      const result = Object.values(dateGroups).map(group => {
        const entries = group.entries;
        const count = entries.length;

        // Helper function to calculate average excluding nulls
        const calculateAverage = (metricName) => {
          // Filter out null values
          const validValues = entries
            .map(entry => entry[metricName])
            .filter(val => val !== null);

          // Calculate average only from valid values
          const validCount = validValues.length;
          if (validCount === 0) return null; // No valid data

          const sum = validValues.reduce((acc, val) => acc + val, 0);
          return sum / validCount;
        };

        return {
          date: group.date,
          rawDate: group.rawDate,
          structure: calculateAverage('structure'),
          coverage: calculateAverage('coverage'),
          length: calculateAverage('length'),
          faithfulness: calculateAverage('faithfulness'),
          count: count // Total count for debugging
        };
      });

      // Filter out dates with no data and sort chronologically
      return result.sort((a, b) => a.rawDate.localeCompare(b.rawDate));

    } catch (error) {
      console.error("Error processing trend data:", error);
      return [];
    }
  }, [data, model]);

  // Format tooltip to show 2 decimal points
  const formatTooltip = (value, name) => {
    if (name === 'count') return value; // Show count as is
    if (value === null) return "No data"; // For null values
    return value.toFixed(2); // Format metrics to 2 decimal places
  };

  // Check if we have useful data to show
  const hasValidData = trendData.length > 1;
  const hasSingleDataPoint = trendData.length === 1;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-4 text-ktx-navy">Performance Trends: {model}</h3>

        {!hasValidData && !hasSingleDataPoint && (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-center">
              No historical data available yet.
              <br />
              Trend charts will appear when data from multiple dates is collected.
            </p>
          </div>
        )}

        {hasSingleDataPoint && (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
            <p className="text-center">
              Only data from a single date is available.
              <br />
              Trend visualization will be enabled when data from multiple dates is collected.
            </p>
          </div>
        )}

        {hasValidData && (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart
              data={trendData}
              margin={{ top: 5, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fill: '#6b7280', fontSize: 12 }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={{ stroke: '#e5e7eb' }}
                padding={{ left: 10, right: 10 }}
              />
              <YAxis
                domain={[0, 1]}
                tick={{ fill: '#6b7280', fontSize: 12 }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={{ stroke: '#e5e7eb' }}
              />
              <Tooltip
                formatter={formatTooltip}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}
              />
              <Legend
                wrapperStyle={{ paddingTop: 15 }}
                iconType="circle"
              />
              <Line
                type="monotone"
                dataKey="structure"
                stroke="#8884d8"
                strokeWidth={2}
                dot={{ r: 4, strokeWidth: 2 }}
                activeDot={{ r: 6, strokeWidth: 2 }}
                name="Structure"
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="coverage"
                stroke="#82ca9d"
                strokeWidth={2}
                dot={{ r: 4, strokeWidth: 2 }}
                activeDot={{ r: 6, strokeWidth: 2 }}
                name="Coverage"
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="length"
                stroke="#ffc658"
                strokeWidth={2}
                dot={{ r: 4, strokeWidth: 2 }}
                activeDot={{ r: 6, strokeWidth: 2 }}
                name="Length"
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="faithfulness"
                stroke="#ff8042"
                strokeWidth={2}
                dot={{ r: 4, strokeWidth: 2 }}
                activeDot={{ r: 6, strokeWidth: 2 }}
                name="Faithfulness"
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {hasValidData && (
        <div className="px-6 pb-4 text-sm text-gray-500">
          Chart shows average scores (0-1 scale) for each metric over time
          <br />
          <span className="text-xs">Gaps indicate dates with no data for that metric</span>
        </div>
      )}
    </div>
  );
}